import { isEmpty } from 'lodash'
import { defineStore } from 'pinia'
import { useApi } from '~/terminierungsportal/api'
import type { ApiClient } from '~/terminierungsportal/api/client'
import { type FeedbackFormForAppointment } from '~/terminierungsportal/api/client'
import type { AppointmentUpdateAction } from '~/terminierungsportal/types/appointmentTypes'
import {
  type AlternativeContact,
  type Appointment,
  AppointmentState,
  type AvailableTimeslot,
  type FormattedTimeslot,
  type OptIns,
  type Timeslot
} from '~/terminierungsportal/types/appointmentTypes'
import type {
  EmailContact,
  PhoneContact,
  PostalCode
} from '~/base/types/contact/contact'
import { useDateFormatter } from '~/terminierungsportal/composables/useDateFormatter'

const { differenceInBusinessDays } = useDate()

interface AppointmentStore {
  appointmentId: string
  authAttempts404: number
  appointment: Appointment
  childAppointment: Appointment
  timeslotSuggestions: AvailableTimeslot[]
  selectedTimeSlot: AvailableTimeslot
  feedbackId: string
  form: FeedbackFormForAppointment
}

const $api = (): ApiClient => useApi()
const daysAllowedBeforeDeadline = 1

export const useAppointmentStore = defineStore('appointment', {
  state: (): AppointmentStore => {
    return {
      appointmentId: '',
      authAttempts404: 0,
      appointment: {} as Appointment,
      childAppointment: {} as Appointment,
      timeslotSuggestions: [] as AvailableTimeslot[],
      selectedTimeSlot: {} as AvailableTimeslot,
      feedbackId: '',
      form: {} as FeedbackFormForAppointment
    }
  },
  getters: {
    availableTimeSlots: (state): AvailableTimeslot[] =>
      state.timeslotSuggestions,

    contactInfo: (state) => state.appointment.contactInfo,

    alternativeContact: (state) => state.appointment.alternativeContact,

    formattedAddress: (state) => {
      const {
        location: { street, houseNr, postalCode, city }
      } = state.appointment

      return `${street.trim()} ${houseNr.trim()}, ${postalCode.trim()} ${city.trim()}`
    },

    formattedTimeslot: (state): FormattedTimeslot => {
      const { formatTimeslots } = useDateFormatter()
      return formatTimeslots(
        state.appointment.timeslot,
        state.appointment.preferredTimeslot
      )
    },

    childFormattedTimeslot: (state): FormattedTimeslot => {
      const { formatTimeslots } = useDateFormatter()
      return formatTimeslots(
        state.childAppointment.timeslot,
        state.childAppointment.preferredTimeslot
      )
    },

    reschedulingIsPossible: (state): boolean => {
      return (
        differenceInBusinessDays(state.appointment.timeslot.from, new Date()) >
        daysAllowedBeforeDeadline
      )
    },

    appointmentHasPassed: (state): boolean => {
      return state.appointment?.timeslot
        ? new Date() >= new Date(state.appointment.timeslot.to)
        : false
    },

    hasAppointmentData: (state) => !isEmpty(state.appointment),

    generalInstructionValid: (state) =>
      !isEmpty(state.appointment.additionalInfos.general),

    phoneInstruction: (state) => state.appointment.additionalInfos.phone,

    hasPhoneInstruction: (state) =>
      !isEmpty(state.appointment.additionalInfos.phone)
  },
  actions: {
    initialize(appointmentId: string): void {
      localStorage.setItem('appointmentId', appointmentId)
      this.appointmentId = appointmentId
    },

    incrementAuthAttempts404(): void {
      this.authAttempts404++
    },

    isAuthenticated() {
      return useApi().isAuthenticated()
    },

    async publishAppointment() {
      await $api().publishAppointment()
    },

    async fetchAppointment() {
      const appointment = await $api().getAppointment()
      if (appointment) {
        this.appointment = appointment
      }
    },

    async fetchChildAppointment() {
      if (
        this.appointment &&
        this.appointment.state === AppointmentState.Delegated
      ) {
        const childAppointment = await $api().getChildAppointment()
        if (childAppointment) {
          this.childAppointment = childAppointment
        }
      }
    },

    async getTimeSlots() {
      const timeslots = await $api().getTimeSlots()
      this.timeslotSuggestions = timeslots
    },

    clearTimeSlots() {
      this.timeslotSuggestions = []
    },

    selectPreferredTimeslot(preferredTimeslot: AvailableTimeslot) {
      this.selectedTimeSlot = preferredTimeslot
    },

    async submitPreferredTimeslot(preferredTimeslot: Timeslot | null) {
      const appointment = await $api().putAppointmentFields({
        preferredTimeslot
      })
      this.appointment = appointment
    },

    async submitContactInfo(
      emailOrPhone: Partial<EmailContact & PhoneContact>
    ) {
      const appointment = await $api().putAppointmentFields({
        contactInfo: {
          ...this.appointment.contactInfo,
          phone: emailOrPhone.phone ? emailOrPhone.phone : '',
          email: emailOrPhone.email ? emailOrPhone.email : ''
        }
      })
      this.appointment = appointment
    },

    async submitGeneralInstruction(general: string) {
      const appointment = await $api().putAppointmentFields({
        additionalInfos: {
          ...this.appointment.additionalInfos,
          general
        }
      })
      this.appointment = appointment
    },

    async submitPhoneInstruction(phone: string) {
      const appointment = await $api().putAppointmentFields({
        additionalInfos: {
          ...this.appointment.additionalInfos,
          phone
        }
      })
      this.appointment = appointment
    },

    async submitAlternativeContact(alternativeContact: AlternativeContact) {
      const appointment = await $api().putAppointmentFields({
        alternativeContact
      })
      this.appointment = appointment
    },

    async submitOptIns(optIns: OptIns) {
      const appointment = await $api().putAppointmentFields({
        optIns
      })
      this.appointment = appointment
    },

    async changeAppointmentState(action: AppointmentUpdateAction) {
      const appointment = await $api().putAppointmentStateUpdate(action)
      this.appointment = appointment
    },

    async delegateAppointment() {
      const appointment = await $api().delegateAppointment()
      this.appointment = appointment
    },

    async fetchFeedbackId() {
      this.feedbackId = await useApi().fetchFeedbackId(this.appointmentId)
    },

    async submitFeedback(form: FeedbackFormForAppointment) {
      this.form = form
      this.feedbackId = await useApi().submitFeedbackForm(form)
    },

    async submitRaffle(contact: EmailContact & PostalCode) {
      await useApi().submitRaffle(this.feedbackId, {
        appointmentId: this.form.appointmentId,
        ...contact
      })
      this.form = { ...this.form, ...contact }
    }
  }
})
